import { useEffect, useRef } from 'react'
import { Routes, Route } from 'react-router-dom'
import { fetchProfileWithTimeout, getSessionWithTimeout, supabase } from './lib/supabase'
import { useAuthStore } from './stores'

// Layouts
import { MainLayout, DashboardLayout } from './components/layout'

// Auth components
import { AuthGuard } from './components/auth'

// Public pages
import {
  HomePage,
  VideoBrowsePage,
  VideoDetailPage,
  ProviderProfilePage,
} from './pages/public'

// Auth pages
import { LoginPage, RegisterPage } from './pages/auth'

// Dashboard pages
import {
  DashboardPage,
  MyVideosPage,
  VideoUploadPage,
  VideoEditPage,
  ProfileSettingsPage,
  SubscriptionPage,
  AnalyticsPage,
  TeamPage,
  ClientsPage,
  ClientDetailPage,
  ProgramsPage,
  ProgramBuilderPage,
  ProgramDetailPage,
  AssignmentsPage,
  AssignmentDetailPage,
  CompliancePage,
  GroupsPage,
  PublicProgramsPage,
  PublicProgramDetailPage,
} from './pages/dashboard'

// Client portal pages (public, token-based auth)
import {
  ClientPortalPage,
  ClientProgramPage,
  ClientVideoPage,
  ClientLoginPage,
  ClientDashboardPage,
  AssignmentAccessPage,
  AssignmentVideoPage,
} from './pages/client'

// Toast provider
import { ToastContainer } from './components/common/Toast'

function App() {
  const { setUser, setSession, setProfile, setIsLoading, setInitialized, profile, _hasHydrated } = useAuthStore()

  const initialResolvedRef = useRef(false)

  const markResolved = () => {
    if (initialResolvedRef.current) return
    initialResolvedRef.current = true
    setIsLoading(false)
    setInitialized(true)
  }

  // Full reset including profile - only call on confirmed no-session or explicit logout
  const resetAllState = () => {
    useAuthStore.persist.clearStorage()
    setSession(null)
    setUser(null)
    setProfile(null)
  }

  useEffect(() => {
    let active = true

    // If we have a cached profile from localStorage, mark initialized immediately
    // This prevents any loading state - cached data shows instantly
    if (_hasHydrated && profile) {
      markResolved()
    }

    // Fallback: ensure we don't hang forever if no cached profile
    const initFallback = setTimeout(() => {
      if (!initialResolvedRef.current) {
        markResolved()
      }
    }, 4000)

    // Load fresh profile data (silently updates cached data)
    const loadProfile = async (userId: string) => {
      try {
        const { data, error } = await fetchProfileWithTimeout(userId)
        if (!active) return
        if (error || !data) return // Keep cached profile on error
        setProfile(data)
      } catch {
        // Keep cached profile on timeout - silent failure
      }
    }

    // Initialize auth - this runs in background if we have cached data
    const initAuth = async () => {
      // Only show loading if we don't have cached profile
      if (!profile) {
        setIsLoading(true)
      }

      try {
        const { data: { session } } = await getSessionWithTimeout()
        if (!active) return

        if (session) {
          setSession(session)
          setUser(session.user)
          await loadProfile(session.user.id)
          markResolved()
        } else {
          // Confirmed no session - clear everything including cached profile
          resetAllState()
          markResolved()
        }
      } catch {
        // On timeout, keep cached profile and mark resolved
        markResolved()
      }
    }

    initAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!active) return

      switch (event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
          if (session) {
            setSession(session)
            setUser(session.user)
            await loadProfile(session.user.id)
          }
          markResolved()
          break
        case 'SIGNED_OUT':
          // Explicit sign out - clear everything including cached profile
          resetAllState()
          markResolved()
          break
        default:
          markResolved()
          break
      }
    })

    return () => {
      active = false
      clearTimeout(initFallback)
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_hasHydrated])

  return (
    <>
      <Routes>
        {/* Public routes with MainLayout */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/videos" element={<VideoBrowsePage />} />
          <Route path="/videos/:id" element={<VideoDetailPage />} />
          <Route path="/providers/:id" element={<ProviderProfilePage />} />
        </Route>

        {/* Auth routes (no layout) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Protected dashboard routes */}
        <Route
          element={
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/videos" element={<MyVideosPage />} />
          <Route
            path="/dashboard/videos/upload"
            element={<VideoUploadPage />}
          />
          <Route
            path="/dashboard/videos/:id/edit"
            element={<VideoEditPage />}
          />
          <Route path="/dashboard/profile" element={<ProfileSettingsPage />} />
          <Route path="/dashboard/subscription" element={<SubscriptionPage />} />
          <Route path="/dashboard/analytics" element={<AnalyticsPage />} />
          <Route path="/dashboard/team" element={<TeamPage />} />

          {/* Phase 2: Clients, Programs, Assignments */}
          <Route path="/dashboard/clients" element={<ClientsPage />} />
          <Route path="/dashboard/clients/:id" element={<ClientDetailPage />} />
          <Route path="/dashboard/programs" element={<ProgramsPage />} />
          <Route path="/dashboard/programs/new" element={<ProgramBuilderPage />} />
          <Route path="/dashboard/programs/:id" element={<ProgramDetailPage />} />
          <Route path="/dashboard/programs/:id/edit" element={<ProgramBuilderPage />} />
          <Route path="/dashboard/assignments" element={<AssignmentsPage />} />
          <Route path="/dashboard/assignments/:id" element={<AssignmentDetailPage />} />

          {/* Phase 5: Compliance Tracking */}
          <Route path="/dashboard/compliance" element={<CompliancePage />} />

          {/* Phase 7: Groups */}
          <Route path="/dashboard/groups" element={<GroupsPage />} />

          {/* Phase 8: Public Programs / Templates */}
          <Route path="/dashboard/public-programs" element={<PublicProgramsPage />} />
          <Route path="/dashboard/templates" element={<PublicProgramsPage />} />
          <Route path="/dashboard/public-programs/:id" element={<PublicProgramDetailPage />} />
          <Route path="/dashboard/templates/:id" element={<PublicProgramDetailPage />} />

          {/* Route alias for Progress (was Compliance) */}
          <Route path="/dashboard/progress" element={<CompliancePage />} />
        </Route>

        {/* Client portal routes (public, token-based auth) */}
        <Route path="/c/:token" element={<ClientPortalPage />} />
        <Route path="/c/:token/program/:id" element={<ClientProgramPage />} />
        <Route path="/c/:token/video/:id/:itemId" element={<ClientVideoPage />} />

        {/* Client login/dashboard routes (for clients with accounts) */}
        <Route path="/client/login" element={<ClientLoginPage />} />
        <Route path="/client/dashboard" element={<ClientDashboardPage />} />

        {/* Direct assignment access (magic link - coach shares with client) */}
        <Route path="/a/:token" element={<AssignmentAccessPage />} />
        <Route path="/a/:token/video/:id/:itemId" element={<AssignmentVideoPage />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Global toast container */}
      <ToastContainer />
    </>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300">404</h1>
        <p className="mt-4 text-xl text-gray-600">Page not found</p>
        <a
          href="/"
          className="mt-6 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go Home
        </a>
      </div>
    </div>
  )
}

export default App
